import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { Activity, RefreshCw, Search } from "lucide-react"
import { smartoltService, type SmartOLTCapacity, type SmartOLTCapacityPort } from "../../../services/smartolt.service.ts"
import { toast } from "../../../utils/notify.ts"
import { SMARTOLT_PATHS } from "../smartoltNav.ts"

const empty: SmartOLTCapacity = {
  ports: [],
  summary: { pon_ports: 0, onus: 0, online: 0, offline: 0, avg_fill: 0, over_threshold: 0, oversub_pct: 90 },
  max_onus: 128,
  oversub_pct: 90,
}

function configuredHref(row: SmartOLTCapacityPort, extra?: Record<string, string>) {
  const query = new URLSearchParams()
  if (row.olt_id) query.set("olt", String(row.olt_id))
  if (row.board) query.set("board", String(row.board))
  if (row.port) query.set("port", String(row.port))
  Object.entries(extra || {}).forEach(([key, value]) => query.set(key, value))
  const text = query.toString()
  return text ? `${SMARTOLT_PATHS.configured}?${text}` : SMARTOLT_PATHS.configured
}

function gbps(value: number) {
  return `${value.toFixed(2).replace(/\.?0+$/, "")}G`
}

export function SmartOLTCapacityPage() {
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [data, setData] = useState<SmartOLTCapacity>(empty)
  const [search, setSearch] = useState("")

  const load = async (silent = false) => {
    if (!silent) setLoading(true)
    setRefreshing(true)
    try {
      const next = await smartoltService.getCapacity()
      setData({
        ports: Array.isArray(next?.ports) ? next.ports : [],
        summary: next?.summary || empty.summary,
        max_onus: next?.max_onus || 128,
        oversub_pct: next?.oversub_pct || 90,
      })
    } catch {
      toast.error("Failed to load PON capacity")
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return data.ports
    return data.ports.filter((row) =>
      [row.olt_name, row.board, row.port, row.pon_name, row.pon_type, row.status]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q),
    )
  }, [data.ports, search])

  return (
    <div className="space-y-4 sm:space-y-5">
      {/* Page header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">PON Capacity</h1>
          <p className="mt-0.5 text-xs text-slate-500">
            Fill % = provisioned ONUs ÷ <strong className="text-slate-700">{data.max_onus}</strong> planned max.
            Ports at or above <strong className="text-slate-700">{data.oversub_pct}%</strong> are flagged.{" "}
            <Link to={SMARTOLT_PATHS.system} className="text-blue-600 font-semibold hover:underline">Tune in Settings</Link>.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-lg bg-slate-100 border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-xs">
            {data.summary.pon_ports} PON ports
          </span>
          <span className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-1.5 text-xs font-semibold text-blue-700 shadow-xs">
            {data.summary.onus} ONUs
          </span>
          <span className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-1.5 text-xs font-semibold text-emerald-700 shadow-xs">
            {data.summary.online} online
          </span>
          <span className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-700 shadow-xs">
            {data.summary.offline} offline
          </span>
          <span className="rounded-lg bg-slate-100 border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-xs">
            avg {data.summary.avg_fill}% full
          </span>
          {data.summary.over_threshold > 0 && (
            <span className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-1.5 text-xs font-semibold text-amber-800 shadow-xs">
              {data.summary.over_threshold} over {data.oversub_pct}%
            </span>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-[11px] text-slate-500">
        <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-500" /> Online ONUs</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-rose-500" /> Offline ONUs</span>
        <span>Upstream / downstream free (estimated GPON 2.488 / 1.244 Gbps)</span>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-xs">
        <div className="sticky top-14 z-10 flex flex-wrap items-center gap-2.5 border-b border-slate-100 bg-slate-50/80 p-3.5 sm:p-4 backdrop-blur-sm">
          <div className="relative min-w-[180px] flex-1 sm:max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search PON ports…"
              className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-900 placeholder-slate-400 shadow-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <button type="button" disabled={refreshing} onClick={() => load()}
            className="ml-auto inline-flex h-9 items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 px-3 text-sm font-semibold text-white shadow-xs disabled:opacity-50 transition-all">
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                {["OLT", "Board", "Port", "PON Name", "Type", "ONUs", "Max", "Fill (online/offline)", "Free BW", "Status"].map((h) => (
                  <th key={h} className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {loading ? (
                <tr><td colSpan={10} className="px-3 py-10 text-center text-slate-500">
                  <div className="flex flex-col items-center gap-2">
                    <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
                    <span className="text-xs">Loading capacity data…</span>
                  </div>
                </td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={10} className="px-3 py-10 text-center text-slate-500 text-sm">No PON ports cached.</td></tr>
              ) : (
                filtered.map((row) => {
                  const over = row.status === "oversubscribed"
                  return (
                    <tr key={`${row.olt_id}-${row.board}-${row.port}`}
                      className={`transition-colors hover:bg-slate-50 ${over ? "bg-amber-50/40" : ""}`}>
                      <td className="px-3 py-2.5 font-medium">
                        <Link to={`${SMARTOLT_PATHS.oltDetail(row.olt_id)}#pon_ports`}
                          className="text-blue-600 hover:text-blue-800 hover:underline">
                          {row.olt_name || row.olt_id}
                        </Link>
                      </td>
                      <td className="px-3 py-2.5 text-slate-500">{row.board || "—"}</td>
                      <td className="px-3 py-2.5 font-medium">
                        <Link to={configuredHref(row)} className="text-blue-600 hover:text-blue-800 hover:underline">{row.port || "—"}</Link>
                      </td>
                      <td className="px-3 py-2.5 text-slate-700">{row.pon_name || "—"}</td>
                      <td className="px-3 py-2.5 uppercase text-slate-500 text-xs font-mono">{row.pon_type || "gpon"}</td>
                      <td className="px-3 py-2.5 text-right font-medium">
                        <Link to={configuredHref(row)} className="text-blue-600 hover:text-blue-800">{row.onus}</Link>
                        {row.onus > 0 && (
                          <span className="ml-1 text-[11px] font-normal">
                            (<Link to={configuredHref(row, { status: "online" })} className="text-emerald-600 hover:text-emerald-800">{row.online}</Link>
                            /<span className="text-rose-600">{row.offline}</span>)
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right text-slate-500">{row.max_onus}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <div className="flex h-2 w-36 overflow-hidden rounded-full bg-slate-100 border border-slate-200" title={`${row.online} online · ${row.offline} offline`}>
                            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${Math.min(100, row.online_fill_pct)}%` }} />
                            <div className="h-full bg-rose-500 transition-all" style={{ width: `${Math.min(100 - row.online_fill_pct, row.offline_fill_pct)}%` }} />
                          </div>
                          <span className={`text-xs font-semibold ${over ? "text-amber-700" : "text-emerald-700"}`}>{row.fill_pct}%</span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="space-y-1 text-[11px] text-slate-500">
                          <div className="flex items-center gap-2" title={`Up: ${row.up_free_gbps} / ${row.up_cap_gbps} Gbps`}>
                            <span className="w-3 text-slate-400">↑</span>
                            <div className="h-1.5 w-16 overflow-hidden rounded-full bg-slate-100 border border-slate-200">
                              <div className={`h-full ${over ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, (row.up_free_gbps / row.up_cap_gbps) * 100)}%` }} />
                            </div>
                            <span>{gbps(row.up_free_gbps)}</span>
                          </div>
                          <div className="flex items-center gap-2" title={`Down: ${row.down_free_gbps} / ${row.down_cap_gbps} Gbps`}>
                            <span className="w-3 text-slate-400">↓</span>
                            <div className="h-1.5 w-16 overflow-hidden rounded-full bg-slate-100 border border-slate-200">
                              <div className={`h-full ${over ? "bg-amber-500" : "bg-blue-500"}`} style={{ width: `${Math.min(100, (row.down_free_gbps / row.down_cap_gbps) * 100)}%` }} />
                            </div>
                            <span>{gbps(row.down_free_gbps)}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${
                          over
                            ? "bg-amber-50 text-amber-700 border border-amber-200"
                            : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                        }`}>
                          {over ? "Oversubscribed" : "OK"}
                        </span>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
